import type { SupabaseClient } from "@supabase/supabase-js";

// On-shift roster read model — public.management_on_shift_roster (active/scheduled + recently-ended
// assignments; redacted guard_label; management-scoped RLS). Read-only. Coverage dials
// (supervisor_route_coverage_summary) are a later enhancement.

export interface RosterRow {
  routeAssignmentId: string;
  routeName: string | null;
  siteId: string | null;
  siteName: string | null;
  assignmentStatus: string;
  guardLabel: string | null;
  /** Redacted member key, shared derivation with free-duty presence. Never a user id. */
  guardRef: string | null;
  startsAt: string | null;
  endsAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function mapRosterRow(raw: Record<string, unknown>): RosterRow {
  return {
    routeAssignmentId: asString(raw.route_assignment_id) ?? "",
    routeName: asString(raw.route_name),
    siteId: asString(raw.site_id),
    siteName: asString(raw.site_name),
    assignmentStatus: asString(raw.assignment_status) ?? "scheduled",
    guardLabel: asString(raw.guard_label),
    // Redacted per-member key from member_user_ref, the SAME derivation
    // list_free_duty_live_presence returns. It is the only thing that lets the desk tell that a
    // roster row and a free-duty row are one human, which is what keeps one person to one mark.
    guardRef: asString(raw.guard_ref),
    startsAt: asString(raw.starts_at),
    endsAt: asString(raw.ends_at),
  };
}

const ROSTER_LIMIT = 50;

/** Fetch the on-shift roster, most recent shift start first. Throws on read error. */
export async function fetchRoster(
  client: SupabaseClient,
  accountId: string,
  limit: number = ROSTER_LIMIT,
): Promise<RosterRow[]> {
  const { data, error } = await client
    .from("management_on_shift_roster")
    .select("*")
    .eq("account_id", accountId)
    .order("starts_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load the roster.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapRosterRow(row as Record<string, unknown>));
}
